import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import {
  Table,
  TableWithOrder,
  TableStatus,
  OrderChannel,
  Order,
  OrderItem,
} from './types'

// ─── Types for backend views ─────────────────────────────────
export interface CashierTableView {
  table_id: string
  restaurant_id: string
  table_number: string
  table_name: string | null
  zone: string | null
  capacity: number | null
  is_active: boolean
  table_status: string
  open_orders_count: number
  open_items_count: number
  open_total: number
  minutes_open: number | null
  order_channel: string | null
  waiter_name: string | null
  current_order_id: string | null
  current_order_status: string | null
}

export interface TableOrderItemView {
  order_item_id: string
  order_id: string
  table_id: string
  item_name: string
  quantity: number
  unit_price: number
  line_total: number
  station_code: string | null
  station_name: string | null
  item_status: string
  notes: string | null
}

export interface OpenTableAccount {
  table_id: string
  table_number: string
  table_name: string | null
  zone: string | null
  order_id: string
  order_number: string
  customer_name: string | null
  order_status: string
  order_channel: string | null
  waiter_name: string | null
  open_total: number
  minutes_open: number
  items_count: number
  created_at: string
}

export interface ActionResult {
  success: boolean
  error?: string
}

// ─── Main Table Management Hook ──────────────────────────────
export function useTableManagement(restaurantId: string | undefined) {
  const [tables, setTables] = useState<TableWithOrder[]>([])
  const [loading, setLoading] = useState(true)

  const mapViewStatus = (viewStatus: string): TableStatus => {
    switch (viewStatus?.toLowerCase()) {
      case 'free':
      case 'available':
        return 'free'
      case 'occupied':
        return 'occupied'
      case 'ordering':
        return 'ordering'
      case 'waiting_payment':
        return 'waiting_payment'
      case 'closed':
        return 'closed'
      default:
        return 'free'
    }
  }

  const mapOrderChannel = (channel: string | null): OrderChannel | null => {
    if (!channel) return null
    const ch = channel.toLowerCase()
    if (ch === 'qr' || ch.includes('self')) return 'qr'
    if (ch === 'waiter' || ch.includes('mesero')) return 'waiter'
    if (ch === 'cashier' || ch.includes('caja')) return 'cashier'
    if (ch === 'web') return 'web'
    return 'web'
  }

  const computeTableStatus = (order: Order | null): TableStatus => {
    if (!order) return 'free'
    switch (order.status) {
      case 'new':
        return 'ordering'
      case 'accepted':
      case 'preparing':
        return 'occupied'
      case 'ready':
        return 'waiting_payment'
      case 'delivered':
      case 'cancelled':
        return 'closed'
      default:
        return 'occupied'
    }
  }

  const getOrderChannel = (order: Order | null): OrderChannel | null => {
    if (!order) return null
    const src = order.source?.toLowerCase() || ''
    if (src.includes('qr') || src.includes('self')) return 'qr'
    if (src.includes('waiter') || src.includes('mesero')) return 'waiter'
    if (src.includes('cashier') || src.includes('caja')) return 'cashier'
    if (src.includes('web') || src.includes('online')) return 'web'
    if (order.table_id && order.delivery_type === 'dine_in') return 'qr'
    return 'web'
  }

  const fetchTablesFromView = useCallback(async (): Promise<boolean> => {
    if (!restaurantId) return false
    try {
      const { data, error } = await supabase
        .from('v_restaurant_tables_cashier')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('table_number')

      if (error) throw error
      if (!data) return false

      const mapped: TableWithOrder[] = data.map((row: any) => ({
        id: row.table_id,
        restaurant_id: row.restaurant_id,
        table_number: row.table_number,
        table_name: row.table_name,
        zone: row.zone,
        capacity: row.capacity,
        qr_code_url: null,
        qr_token: null,
        is_active: row.is_active ?? true,
        created_at: '',
        updated_at: '',
        current_order: row.current_order_id
          ? ({
              id: row.current_order_id,
              order_number: row.order_number || '',
              status: row.current_order_status || 'new',
              total: row.open_total || 0,
              customer_name: row.customer_name || '',
              created_at: row.order_created_at || '',
            } as any)
          : null,
        current_order_items: [],
        status: mapViewStatus(row.table_status),
        order_channel: mapOrderChannel(row.order_channel),
        time_open_minutes: row.minutes_open ?? null,
        item_count: row.open_items_count || 0,
        waiter_name: row.waiter_name || null,
      }))

      setTables(mapped)
      return true
    } catch {
      return false
    }
  }, [restaurantId])

  const fetchTablesFallback = useCallback(async () => {
    if (!restaurantId) return
    try {
      const { data: tablesData, error: tablesError } = await supabase
        .from('tables')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .order('table_number')

      if (tablesError) throw tablesError
      if (!tablesData || tablesData.length === 0) {
        setTables([])
        return
      }

      const { data: ordersData } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .not('table_id', 'is', null)
        .not('status', 'in', '("delivered","cancelled")')
        .order('created_at', { ascending: false })

      const activeOrders = ordersData || []
      const orderIds = activeOrders.map((o) => o.id)
      let allItems: OrderItem[] = []
      if (orderIds.length > 0) {
        const { data: itemsData } = await supabase
          .from('order_items')
          .select('*')
          .in('order_id', orderIds)

        allItems = itemsData || []
      }

      const tablesWithOrders: TableWithOrder[] = tablesData.map(
        (table: Table) => {
          const currentOrder =
            activeOrders.find((o) => o.table_id === table.id) || null
          const orderItems = currentOrder
            ? allItems.filter((i) => i.order_id === currentOrder.id)
            : []
          const timeOpen = currentOrder
            ? Math.floor(
                (new Date().getTime() -
                  new Date(currentOrder.created_at).getTime()) /
                  60000,
              )
            : null

          return {
            ...table,
            current_order: currentOrder,
            current_order_items: orderItems,
            status: computeTableStatus(currentOrder),
            order_channel: getOrderChannel(currentOrder),
            time_open_minutes: timeOpen,
            item_count: orderItems.reduce((sum, i) => sum + i.quantity, 0),
          }
        },
      )

      setTables(tablesWithOrders)
    } catch (error) {
      console.error('[NOIZA] Fallback table fetch error:', error)
    }
  }, [restaurantId])

  const fetchTables = useCallback(async () => {
    if (!restaurantId) return
    try {
      const viewWorked = await fetchTablesFromView()
      if (!viewWorked) {
        await fetchTablesFallback()
      }
    } catch {
      await fetchTablesFallback()
    } finally {
      setLoading(false)
    }
  }, [restaurantId, fetchTablesFromView, fetchTablesFallback])

  useEffect(() => {
    fetchTables()
    const interval = setInterval(fetchTables, 8000)
    return () => clearInterval(interval)
  }, [fetchTables])

  return { tables, loading, refetch: fetchTables }
}

// ─── Table Order Items Hook ──────────────────────────────────
export function useTableOrderItems(
  tableId: string | undefined,
  restaurantId: string | undefined,
) {
  const [items, setItems] = useState<TableOrderItemView[]>([])
  const [loading, setLoading] = useState(false)

  const fetchItems = useCallback(async () => {
    if (!tableId || !restaurantId) return
    setLoading(true)
    try {
      // Try the view first
      const { data, error } = await supabase
        .from('v_table_order_items')
        .select('*')
        .eq('table_id', tableId)

      if (!error && data) {
        setItems(
          data.map((row: any) => ({
            order_item_id: row.order_item_id || row.id,
            order_id: row.order_id,
            table_id: row.table_id,
            item_name: row.item_name || row.item_name_snapshot,
            quantity: row.quantity,
            unit_price: row.unit_price || row.unit_price_snapshot || 0,
            line_total: row.line_total,
            station_code: row.station_code,
            station_name: row.station_name || row.station_code || null,
            item_status: row.item_status || row.status || 'pending',
            notes: row.notes,
          })),
        )
        return
      }
    } catch {
      // View doesn't exist, fallback
    }

    // Fallback: query order_items via orders
    try {
      const { data: orders } = await supabase
        .from('orders')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('table_id', tableId)
        .not('status', 'in', '("delivered","cancelled")')

      if (orders && orders.length > 0) {
        const orderIds = orders.map((o) => o.id)
        const { data: itemsData } = await supabase
          .from('order_items')
          .select('*')
          .in('order_id', orderIds)

        setItems(
          (itemsData || []).map((item) => ({
            order_item_id: item.id,
            order_id: item.order_id,
            table_id: tableId,
            item_name: item.item_name_snapshot,
            quantity: item.quantity,
            unit_price: item.unit_price_snapshot,
            line_total: item.line_total,
            station_code: item.station_code,
            station_name: item.station_code,
            item_status: item.status,
            notes: item.notes,
          })),
        )
      } else {
        setItems([])
      }
    } catch (err) {
      console.error('[NOIZA] Error fetching table items:', err)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [tableId, restaurantId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  return { items, loading, refetch: fetchItems }
}

// ─── Close Table Account ─────────────────────────────────────
export async function closeTableAccount(
  restaurantId: string,
  tableId: string,
  paymentMethod: string,
  closedByUserId: string | null,
): Promise<ActionResult> {
  try {
    const { error } = await supabase.rpc('close_table_account', {
      p_restaurant_id: restaurantId,
      p_table_id: tableId,
      p_payment_method: paymentMethod,
      p_closed_by_user_id: closedByUserId || null,
    })

    if (error) {
      // If RPC doesn't exist, fallback to manual close
      if (error.message?.includes('does not exist') || error.code === '42883') {
        return await closeTableAccountFallback(
          restaurantId,
          tableId,
          paymentMethod,
        )
      }
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err: any) {
    return { success: false, error: err?.message || 'Error al cerrar cuenta' }
  }
}

async function closeTableAccountFallback(
  restaurantId: string,
  tableId: string,
  paymentMethod: string,
): Promise<ActionResult> {
  try {
    // Find active orders for this table
    const { data: orders, error: fetchErr } = await supabase
      .from('orders')
      .select('id, status')
      .eq('restaurant_id', restaurantId)
      .eq('table_id', tableId)
      .not('status', 'in', '("delivered","cancelled")')

    if (fetchErr) throw fetchErr
    if (!orders || orders.length === 0) {
      return { success: false, error: 'No hay cuenta abierta en esta mesa' }
    }

    // Mark all active orders as delivered
    for (const order of orders) {
      const { error: updateErr } = await supabase
        .from('orders')
        .update({
          status: 'delivered',
          payment_method: paymentMethod,
          updated_at: new Date().toISOString(),
        })
        .eq('id', order.id)

      if (updateErr) {
        console.error('[NOIZA] Error closing order:', updateErr)
      }
    }

    return { success: true }
  } catch (err: any) {
    return { success: false, error: err?.message || 'Error al cerrar cuenta' }
  }
}

// ─── Table Metrics Hook ──────────────────────────────────────
export interface TableMetrics {
  qrOrdersToday: number
  waiterOrdersToday: number
  selfServicePercent: number
  waiterPercent: number
  activeTables: number
  totalTables: number
  averageTicketPerTable: number
  totalRevenueToday: number
}

export function useTableMetrics(restaurantId: string | undefined) {
  const [metrics, setMetrics] = useState<TableMetrics>({
    qrOrdersToday: 0,
    waiterOrdersToday: 0,
    selfServicePercent: 0,
    waiterPercent: 0,
    activeTables: 0,
    totalTables: 0,
    averageTicketPerTable: 0,
    totalRevenueToday: 0,
  })
  const [loading, setLoading] = useState(true)

  const fetchMetricsFromViews = useCallback(async (): Promise<boolean> => {
    if (!restaurantId) return false
    try {
      const [channelRes, pctRes, activeRes, avgRes] = await Promise.all([
        supabase
          .from('v_service_channel_today')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('v_service_channel_today_pct')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('v_active_tables_today')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .single(),
        supabase
          .from('v_avg_ticket_per_table_today')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .single(),
      ])

      // If any critical view fails, fall back
      if (channelRes.error && activeRes.error) return false

      let qr = 0,
        waiter = 0,
        qrPct = 0,
        waiterPct = 0
      const channels = channelRes.data || []
      channels.forEach((row: any) => {
        const ch = (row.order_channel || row.channel || '').toLowerCase()
        if (ch === 'qr') qr = row.order_count || row.orders_count || 0
        if (ch === 'waiter' || ch === 'mesero')
          waiter = row.order_count || row.orders_count || 0
      })

      const pcts = pctRes.data || []
      pcts.forEach((row: any) => {
        const ch = (row.order_channel || row.channel || '').toLowerCase()
        if (ch === 'qr') qrPct = Math.round(row.pct || row.percentage || 0)
        if (ch === 'waiter' || ch === 'mesero')
          waiterPct = Math.round(row.pct || row.percentage || 0)
      })

      const activeTables =
        activeRes.data?.active_tables || activeRes.data?.count || 0
      const totalTables = activeRes.data?.total_tables || 0
      const avgTicket =
        avgRes.data?.avg_ticket || avgRes.data?.average_ticket || 0

      setMetrics({
        qrOrdersToday: qr,
        waiterOrdersToday: waiter,
        selfServicePercent: qrPct,
        waiterPercent: waiterPct,
        activeTables,
        totalTables,
        averageTicketPerTable: Math.round(avgTicket),
        totalRevenueToday: 0,
      })

      return true
    } catch {
      return false
    }
  }, [restaurantId])

  const fetchMetricsFallback = useCallback(async () => {
    if (!restaurantId) return
    try {
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)

      const [ordersRes, tablesRes, activeRes] = await Promise.all([
        supabase
          .from('orders')
          .select(
            'id, restaurant_id, status, total, table_id, delivery_type, source, created_at',
          )
          .eq('restaurant_id', restaurantId)
          .gte('created_at', todayStart.toISOString())
          .not('status', 'eq', 'cancelled'),
        supabase
          .from('tables')
          .select('id')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true),
        supabase
          .from('orders')
          .select('table_id')
          .eq('restaurant_id', restaurantId)
          .not('table_id', 'is', null)
          .not('status', 'in', '("delivered","cancelled")'),
      ])

      const orders = ordersRes.data || []
      const totalTables = tablesRes.data?.length || 0
      const activeTableIds = new Set(
        (activeRes.data || []).map((o: any) => o.table_id).filter(Boolean),
      )

      let qrCount = 0,
        waiterCount = 0,
        tableRevenue = 0
      orders.forEach((order: any) => {
        const src = (order.source || '').toLowerCase()
        const isDineIn = order.delivery_type === 'dine_in' || order.table_id
        if (!isDineIn) return

        if (
          src.includes('qr') ||
          src.includes('self') ||
          (order.table_id &&
            !src.includes('waiter') &&
            !src.includes('mesero') &&
            !src.includes('caja'))
        ) {
          qrCount++
        } else if (src.includes('waiter') || src.includes('mesero')) {
          waiterCount++
        } else if (order.table_id) {
          qrCount++
        }
        if (order.table_id) tableRevenue += order.total || 0
      })

      const totalDineIn = qrCount + waiterCount
      const dineInWithTable = orders.filter((o: any) => o.table_id).length

      setMetrics({
        qrOrdersToday: qrCount,
        waiterOrdersToday: waiterCount,
        selfServicePercent:
          totalDineIn > 0 ? Math.round((qrCount / totalDineIn) * 100) : 0,
        waiterPercent:
          totalDineIn > 0 ? Math.round((waiterCount / totalDineIn) * 100) : 0,
        activeTables: activeTableIds.size,
        totalTables,
        averageTicketPerTable:
          dineInWithTable > 0 ? Math.round(tableRevenue / dineInWithTable) : 0,
        totalRevenueToday: tableRevenue,
      })
    } catch (error) {
      console.error('[NOIZA] Fallback metrics error:', error)
    }
  }, [restaurantId])

  useEffect(() => {
    if (!restaurantId) return

    async function fetchMetrics() {
      setLoading(true)
      try {
        const viewWorked = await fetchMetricsFromViews()
        if (!viewWorked) await fetchMetricsFallback()
      } catch {
        await fetchMetricsFallback()
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
    const interval = setInterval(fetchMetrics, 60000)
    return () => clearInterval(interval)
  }, [restaurantId, fetchMetricsFromViews, fetchMetricsFallback])

  return { metrics, loading }
}
